/**
 * Desktop TabPanelList title resolution.
 *
 * Canonical tab fields (additive):
 *   translationKey?      — i18n key, resolved at render with current locale
 *   translationParams?   — raw business params (names, ids); never translated
 *   label / legacyLabel  — snapshot fallback for unmigrated callers
 *
 * Prefer translationKey for system titles so SK→EN→DE updates live without
 * recreating tabs or panels.
 */

/**
 * @param {{ translationKey?: string, translationParams?: object, label?: string, legacyLabel?: string } | null | undefined} tab
 * @param {(key: string, params?: object|null) => string} t
 * @returns {string}
 */
export function resolveTabLabel(tab, t) {
  if (!tab) return '';
  const key = tab.translationKey;
  if (key && typeof t === 'function') {
    try {
      const translated = t(key, tab.translationParams || null);
      if (translated != null && String(translated).trim() !== '') return String(translated);
    } catch (_) {
      /* fall through to snapshot */
    }
  }
  const fallback = tab.label ?? tab.legacyLabel;
  return fallback == null ? '' : String(fallback);
}

/**
 * Build a create_new_tab-compatible action payload fragment for semantic titles.
 * Always includes `name` (legacy snapshot) for favorites / unmigrated consumers.
 *
 * @param {object} opts
 * @param {string} opts.name — resolved snapshot (legacy)
 * @param {string} [opts.translationKey]
 * @param {object} [opts.translationParams]
 * @param {string} [opts.legacyLabel]
 */
export function buildSemanticTabTitleFields({ name, translationKey, translationParams, legacyLabel }) {
  const out = {
    name,
    legacyLabel: legacyLabel != null ? legacyLabel : name,
  };
  if (translationKey) {
    out.translationKey = translationKey;
    if (translationParams && typeof translationParams === 'object') {
      out.translationParams = translationParams;
    }
  }
  return out;
}
